import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../services/api';

export type LanguageLevel = 'basic' | 'standard' | 'advanced';

interface LanguageContextType {
  level: LanguageLevel;
  setLevel: (level: LanguageLevel) => Promise<void>;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [level, setLevelState] = useState<LanguageLevel>('advanced');
  const [isLoading, setIsLoading] = useState(true);

  // Load initial language level
  useEffect(() => {
    loadLanguageLevel();

    // Listen for changes to user preferences in sessionStorage
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'connectpath_user_data' && e.newValue) {
        try {
          const userData = JSON.parse(e.newValue);
          const newLevel = userData.preferences?.language?.level || 'advanced';
          setLevelState(newLevel);
        } catch (error) {
          console.error('Failed to parse user data from storage event:', error);
        }
      }
    };

    // Listen for custom events when preferences are updated
    const handleUserUpdate = () => {
      loadLanguageLevel();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userPreferencesUpdated', handleUserUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userPreferencesUpdated', handleUserUpdate);
    };
  }, []);

  const loadLanguageLevel = async () => {
    try {
      // Load from sessionStorage where AuthContext stores user preferences
      const userDataStr = sessionStorage.getItem('connectpath_user_data');
      if (userDataStr) {
        const userData = JSON.parse(userDataStr);
        const level = userData.preferences?.language?.level || 'advanced';
        setLevelState(level);
        setIsLoading(false);
        return;
      }

      // Fallback: Load from API if sessionStorage doesn't have data
      const token = localStorage.getItem('token');
      if (!token) {
        setLevelState('advanced');
        setIsLoading(false);
        return;
      }

      const response = await api.get('/auth/users/me/');
      const level = response.data.preferences?.language?.level || 'advanced';
      setLevelState(level);
    } catch (error) {
      // Silently default to advanced if load fails
      setLevelState('advanced');
    } finally {
      setIsLoading(false);
    }
  };

  const setLevel = async (newLevel: LanguageLevel) => {
    try {
      setIsLoading(true);
      // Get current user to access their ID and current preferences
      const userResponse = await api.get('/auth/users/me/');
      const userId = userResponse.data.id;
      const currentPreferences = userResponse.data.preferences || {};

      // Update preferences with new language level
      const updatedPreferences = {
        ...currentPreferences,
        language: {
          level: newLevel
        }
      };

      await api.patch(`/auth/users/${userId}/`, { preferences: updatedPreferences });
      setLevelState(newLevel);
    } catch (error) {
      console.error('Failed to update language level:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LanguageContext.Provider value={{ level, setLevel, isLoading }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
